import { FC, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useMediaQuery, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import HomeIcon from '@mui/icons-material/Home';
import { RENTER_PATHS } from 'src/routes/renterPaths';
import { DV_PATHS } from 'src/routes/dataVaultPaths';
import GoogleFontIcon from 'src/icons/GoogleFontIcon';
import RentalsIcon from 'src/icons/RentalsIcon';
import { NavItem } from 'src/types/NavBarTypes';
import { RootState } from 'src/redux/rootReducerType';
import type { Certification } from 'src/types/Certification';
import { getRequestedDocs } from 'src/components/NewRenterInterface/Signatures/signatureFilters';
import { memberNeedsActionFromStatus } from 'src/components/NewRenterInterface/Household/householdSurveyUtils';
import { useRenterAppointments } from 'src/components/NewRenterInterface/Appointments/useRenterAppointments';
import RenterNavBarMobile from './RenterNavBarMobile';
import RenterNavBarDesktop from './RenterNavBarDesktop';

export {
  NAV_WIDTH_EXPANDED,
  NAV_WIDTH_COLLAPSED,
  STORAGE_KEY,
} from './NavBarConstants';

const RenterNavBar: FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  ) as Certification[];

  const certStatus = useSelector(
    (state: RootState) => state.newRenterInterface.certStatus ?? {}
  );

  const userId =
    useSelector((state: RootState) => state.newRenterInterfaceUser.user?.id) ??
    0;

  const selectedHomePageMemberId = useSelector(
    (state: RootState) =>
      state.newRenterInterfaceUser.accountHub.selectedHomePageMemberId
  );

  // Use the selected home page member if set, otherwise fall back to logged-in user.
  const effectiveUserId = selectedHomePageMemberId ?? userId;

  const pendingSigCount = useMemo(
    () => getRequestedDocs(certifications, userId).length,
    [certifications, userId]
  );

  // Count active certifications that have pending actions for the effective user.
  // Uses the same logic as CertificationCard so the badge matches what's shown on cards.
  const certsNeedingActionCount = useMemo(() => {
    return certifications.filter((cert) => {
      if (cert.status?.renter_active === false) {
        return false;
      }
      const currentMember = cert.cert_household?.cert_members?.find(
        (m) => m.user_id === effectiveUserId
      );
      return memberNeedsActionFromStatus(
        certStatus[cert.id],
        effectiveUserId,
        currentMember?.cert_member_id
      );
    }).length;
  }, [certifications, certStatus, effectiveUserId]);

  // Uses the same hook as the appointments surfaces; the backend 404s silently
  // when appointments are off, so the item simply shows no badge.
  const { upcoming: upcomingAppointments } = useRenterAppointments();
  const upcomingAppointmentCount = upcomingAppointments.length;

  const navItems: NavItem[] = useMemo(
    () => [
      {
        label: 'ESign',
        path: `/${RENTER_PATHS.HOME}/${RENTER_PATHS.SIGNATURES}`,
        icon: <GoogleFontIcon name="ink_pen" />,
        badge: pendingSigCount,
      },
      {
        label: 'Home',
        path: `/${RENTER_PATHS.HOME}`,
        icon: <HomeIcon />,
        dotBadge: certsNeedingActionCount > 0,
      },
      {
        label: 'Appointments',
        path: `/${RENTER_PATHS.HOME}/${RENTER_PATHS.APPOINTMENTS}`,
        icon: <GoogleFontIcon name="calendar_month" />,
        badge: upcomingAppointmentCount,
      },
      {
        label: 'My Data',
        path: `/${RENTER_PATHS.HOME}/${DV_PATHS.ROOT}`,
        icon: <GoogleFontIcon name="shield_person" size={20} />,
      },
      {
        label: 'Rentals',
        path: 'https://www.prontohousingrentals.com/',
        icon: <RentalsIcon />,
        external: true,
      },
    ],
    [pendingSigCount, certsNeedingActionCount, upcomingAppointmentCount]
  );
  const handleNavigate = useCallback(
    (path: string, external?: boolean) => {
      if (external) {
        window.open(path, '_blank', 'noopener,noreferrer');
      } else {
        navigate(path);
      }
    },
    [navigate]
  );

  const homePath = `/${RENTER_PATHS.HOME}`;

  const isItemActive = useCallback(
    (item: NavItem): boolean => {
      if (item.external) {
        return false;
      }
      if (item.path === homePath) {
        return (
          pathname === homePath ||
          pathname === `${homePath}/` ||
          pathname.includes('/certification/')
        );
      }
      if (item.path.endsWith(`/${RENTER_PATHS.SIGNATURES}`)) {
        return pathname.includes('/signatures');
      }
      if (item.path.endsWith(`/${RENTER_PATHS.HISTORY}`)) {
        return pathname.includes('/history/') || pathname.endsWith('/history');
      }
      return pathname.startsWith(item.path);
    },
    [pathname, homePath]
  );

  const activeIndex = navItems.findIndex(isItemActive);

  if (isMobile) {
    return (
      <RenterNavBarMobile
        navItems={navItems}
        activeIndex={activeIndex}
        onNavigate={handleNavigate}
      />
    );
  }

  return (
    <RenterNavBarDesktop
      navItems={navItems}
      onNavigate={handleNavigate}
      isItemActive={isItemActive}
    />
  );
};

export default RenterNavBar;
